import LinkIcon from '@mui/icons-material/Link'
import {IconButton, Tooltip} from '@mui/material'
import type {FC, ReactElement} from 'react'
import {useCallback} from 'react'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation.ts'
import {useSnackbar} from '@shared/hooks/useSnackbar'
import {buildTeamsDeepLink} from '@shared/utils/teamsDeepLink'

// Teams-only: Teams' own top chrome has no hook for a custom action button --
// only more staticTabs (each a full page navigation) or a bot's fixed "Chat"
// tab -- so a page-link-copy button has to live in our own appbar instead.
// Only rendered by the parent when isRunningInTeams() is true.
const AppbarCopyLink: FC = (): ReactElement => {
  const [t] = useCustomTranslation('common')
  const [showSnackbar] = useSnackbar()

  const handleCopyLink = useCallback((): void => {
    const copy = async (): Promise<void> => {
      // A plain window.location.href opens in a browser if pasted into a
      // Teams chat -- useless for a colleague who never uses one. The deep
      // link reopens this same in-app page inside the Teams tab itself.
      // Falls back to the plain URL only if VITE_TEAMS_APP_ID never made it
      // into this build (should not happen for a real deployment).
      const link = buildTeamsDeepLink() ?? window.location.href
      await navigator.clipboard.writeText(link)
      showSnackbar({message: t('appbar.link_copied'), severity: 'success'})
    }
    void copy()
  }, [showSnackbar, t])

  return (
    <Tooltip title={t('appbar.copy_link')} arrow>
      <IconButton
        onClick={handleCopyLink}
        data-testid="mbai-appbar-copy-link-button"
        data-cy="mbai-appbar-copy-link-button"
      >
        <LinkIcon />
      </IconButton>
    </Tooltip>
  )
}

export default AppbarCopyLink
